/**
 * Pure per-currency money aggregation for the Student card KPI strip (Area 5).
 *
 * `buildStudentMoney` projects a student's agreements into one totals row per
 * distinct currency among the NON-terminated agreements, coercing Django's
 * string-serialised decimals to numbers before summing. Decoupled from
 * React/Apollo so the per-currency invariants are property-tested in isolation
 * (design Properties 10–11).
 */

/** The minimal agreement shape the money aggregation needs. */
export interface IMoneyAgreement {
  currency?: string | null;
  budgetAmount?: number | string | null;
  paidAmount?: number | string | null;
  terminated?: boolean | null;
}

/** Per-currency Budget/Paid/Debt totals over a student's active agreements. */
export interface ICurrencyTotals {
  currency: string;
  budget: number;
  /** success accent in the strip */
  paid: number;
  /** warn/danger accent when > 0 */
  debt: number;
  agreementsActive: number;
}

/** Coerce a possibly string/null decimal to a finite number (0 fallback). */
const toNumber = (value: number | string | null | undefined): number => {
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : 0;
};

/**
 * Build the per-currency totals.
 *
 * - One row per distinct currency appearing in a non-terminated agreement; a
 *   currency present only in terminated agreements yields no row (Req 14.1).
 * - Terminated agreements are excluded from every total; every non-terminated
 *   agreement is included regardless of any other inactive status (Req 15.3).
 * - String decimals are coerced before summing; `debt = max(0, budget − paid)`
 *   per currency, never borrowing across currencies (Req 15.2, Property 10).
 * - Rows are ordered by descending budget so the largest exposure reads first.
 */
export const buildStudentMoney = (
  agreements: ReadonlyArray<IMoneyAgreement | null | undefined>,
): ICurrencyTotals[] => {
  const map = new Map<string, ICurrencyTotals>();

  (agreements || []).forEach((a) => {
    if (!a || a.terminated) return;
    const currency = a.currency || '';
    const prev =
      map.get(currency) ||
      ({
        currency,
        budget: 0,
        paid: 0,
        debt: 0,
        agreementsActive: 0,
      } as ICurrencyTotals);

    const budget = toNumber(a.budgetAmount);
    const paid = toNumber(a.paidAmount);

    prev.budget += budget;
    prev.paid += paid;
    prev.debt += Math.max(0, budget - paid);
    prev.agreementsActive += 1;
    map.set(currency, prev);
  });

  return Array.from(map.values()).sort((x, y) => y.budget - x.budget);
};
